import React, { useState, useEffect, useCallback } from 'react';
import SelectField from '../../common/SelectField.js';
import Alert from '../../common/Alert.js';
import whatsappPaymentService from '../../../services/WhatsAppPaymentService.js';
import { X, Save, DollarSign } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../../common/Validation.js';
import Button from '../../common/Button.js';


const EditPaymentModal = ({ paymentId, dueAmount = 0, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [payment, setPayment] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'cash',
    payment_status: 'completed',
    payment_date: '',
    transaction_id: '',
    bank_name: '',
    cheque_number: '',
    upi_id: '',
    remarks: ''
  });

  const [receipt, setReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const [existingReceipt, setExistingReceipt] = useState(null);

  const { errors, validate, validateField } = useValidation();

  const validationRules = {
    amount:       (v) => validators.required(v, 'Amount') || validators.number(v, { min: 0.01, label: 'Amount' }),
    payment_date: (v) => validators.required(v, 'Payment date'),
    bank_name:    (v) => (paymentData.payment_method === 'bank_transfer' && v) ? validators.textOnly(v, 'Bank name') : '',
    cheque_number:(v) => (paymentData.payment_method === 'cheque' && v) ? validators.alphanumeric(v, 'Cheque number') : '',
  };

  const fetchPayment = useCallback(async () => {
    try {
      setLoading(true);
      const response = await whatsappPaymentService.getPaymentById(paymentId);
      const paymentDetails = response.data;
      
      setPayment(paymentDetails);
      setPaymentData({
        amount: paymentDetails.amount || '',
        payment_method: paymentDetails.payment_method || 'cash',
        payment_status: paymentDetails.payment_status || 'completed',
        payment_date: paymentDetails.payment_date || '',
        transaction_id: paymentDetails.transaction_id || '',
        bank_name: paymentDetails.bank_name || '',
        cheque_number: paymentDetails.cheque_number || '',
        upi_id: paymentDetails.upi_id || '',
        remarks: paymentDetails.remarks || ''
      });

      if (paymentDetails.has_receipt) {
        setExistingReceipt({
          name: paymentDetails.receipt_name || `receipt_${paymentId}`,
          url: paymentDetails.receipt_url
        });
      }
    } catch (err) {
      console.error('Error fetching payment:', err);
      Alert.error('Failed to load payment details');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [paymentId, onClose]);

  useEffect(() => {
    fetchPayment();
  }, [fetchPayment]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'amount') filtered = inputFilters.amount(value);

    setPaymentData(prev => ({ ...prev, [name]: filtered }));

    if (validationRules[name]) {
      validateField(name, filtered, validationRules[name]);
    }
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Receipt file must be less than 5MB');
        return;
      }
      setReceipt(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeNewReceipt = () => {
    setReceipt(null);
    setReceiptPreview(null);
    const fileInput = document.getElementById('receipt-input-edit-modal');
    if (fileInput) fileInput.value = '';
  };

  const removeExistingReceipt = () => {
    setExistingReceipt(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate(paymentData, validationRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    // For edit: available due = current due + original payment amount (since original was already applied)
    const originalAmount = parseFloat(payment?.amount || 0);
    const maxAllowed = dueAmount + originalAmount;
    if (maxAllowed > 0 && parseFloat(paymentData.amount) > maxAllowed) {
      Alert.error(`Payment amount ₹${parseFloat(paymentData.amount).toFixed(2)} exceeds the balance due of ₹${maxAllowed.toFixed(2)}`);
      return;
    }

    setSubmitting(true);

    try {
      const dataToSend = {
        amount: parseFloat(paymentData.amount),
        payment_method: paymentData.payment_method,
        payment_status: paymentData.payment_status,
        payment_date: paymentData.payment_date,
        transaction_id: paymentData.transaction_id || null,
        bank_name: paymentData.bank_name || null,
        cheque_number: paymentData.cheque_number || null,
        upi_id: paymentData.upi_id || null,
        remarks: paymentData.remarks || null
      };

      await whatsappPaymentService.updatePayment(paymentId, dataToSend, receipt);

      Alert.success('Payment updated successfully!');
      onSuccess();
    } catch (err) {
      console.error('Error updating payment:', err);
      Alert.error(err.response?.data?.error || 'Failed to update payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full text-center">
          <p className="text-gray-600">Loading payment details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Edit Payment</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Amount (₹) *
            </label>
            <input
              type="text"
              name="amount"
              value={paymentData.amount}
              onChange={handleChange}
              required
              placeholder="0.00"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${errors.amount ? 'border-red-400' : 'border-gray-300'}`}
            />
            <ValidationMessage error={errors.amount} />
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Method
            </label>
            <SelectField variant="form"
              name="payment_method"
              value={paymentData.payment_method}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="cash">Cash</option>
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="upi">UPI</option>
              <option value="online">Online</option>
              <option value="other">Other</option>
            </SelectField>
          </div>

          {/* Payment Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Status
            </label>
            <SelectField variant="form"
              name="payment_status"
              value={paymentData.payment_status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </SelectField>
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payment Date
            </label>
            <input
              type="date"
              name="payment_date"
              value={paymentData.payment_date}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${errors.payment_date ? 'border-red-400' : 'border-gray-300'}`}
            />
            <ValidationMessage error={errors.payment_date} />
          </div>

          {/* Transaction ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transaction ID / Reference
            </label>
            <input
              type="text"
              name="transaction_id"
              value={paymentData.transaction_id}
              onChange={handleChange}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Bank Name (if bank transfer) */}
          {paymentData.payment_method === 'bank_transfer' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Bank Name
              </label>
              <input
                type="text"
                name="bank_name"
                value={paymentData.bank_name}
                onChange={handleChange}
                placeholder="Bank name"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${errors.bank_name ? 'border-red-400' : 'border-gray-300'}`}
              />
              <ValidationMessage error={errors.bank_name} />
            </div>
          )}

          {/* Cheque Number (if cheque) */}
          {paymentData.payment_method === 'cheque' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Cheque Number
              </label>
              <input
                type="text"
                name="cheque_number"
                value={paymentData.cheque_number}
                onChange={handleChange}
                placeholder="Cheque number"
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${errors.cheque_number ? 'border-red-400' : 'border-gray-300'}`}
              />
              <ValidationMessage error={errors.cheque_number} />
            </div>
          )}

          {/* UPI ID (if UPI) */}
          {paymentData.payment_method === 'upi' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                UPI ID
              </label>
              <input
                type="text"
                name="upi_id"
                value={paymentData.upi_id}
                onChange={handleChange}
                placeholder="UPI ID"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
            </div>
          )}

          {/* Existing Receipt */}
          {existingReceipt && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Receipt
              </label>
              <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg text-sm border border-green-200">
                <span className="text-gray-700 truncate">{existingReceipt.name}</span>
                <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                  {existingReceipt.url && (
                    <a
                      href={existingReceipt.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-800 text-xs underline"
                    >
                      View
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={removeExistingReceipt}
                    className="text-red-600 hover:text-red-800"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* New Receipt Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Replace Receipt (Optional)
            </label>
            {!receiptPreview ? (
              <input
                type="file"
                id="receipt-input-edit-modal"
                onChange={handleReceiptChange}
                accept=".pdf,.jpg,.jpeg,.png"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            ) : (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg text-sm border border-blue-200">
                <span className="text-gray-700 truncate">{receipt?.name}</span>
                <button
                  type="button"
                  onClick={removeNewReceipt}
                  className="text-red-600 hover:text-red-800 flex-shrink-0 ml-2"
                >
                  ✕
                </button>
              </div>
            )}
          </div>

          {/* Remarks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks
            </label>
            <textarea
              name="remarks"
              value={paymentData.remarks}
              onChange={handleChange}
              placeholder="Any payment notes..."
              rows="2"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <Button variant="primary" type="submit" loading={submitting} icon={Save} className="flex-1">
              {submitting ? 'Updating...' : 'Update Payment'}
            </Button>
            <Button variant="secondary" onClick={onClose} className="flex-1">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPaymentModal;
